import { useEffect, useMemo, useState } from "react";
import { Pressable, Text, View } from "react-native";
import { useTranslation } from "react-i18next";
import { useLocale } from "../primitives/useLocale";
import {
  nextSlotFor,
  pickAuthoredText,
  type Exercise,
  type Program as ProgramT,
  type ProgramProgress as ProgramProgressT,
} from "@jackedlogs/shared";

import type { UiTheme } from "../theme";
import { Icon } from "../Icon/Icon";
import { IconButton } from "../primitives/IconButton";
import { Chip } from "../primitives/Chip";
import { ProgramPreview } from "./ProgramPreview";
import { makePlanStyles } from "./programsPlanStyles";

// One FOLLOWED program's card in the "In corso" section. Compact hero
// (Antenore 2026-07-01): a medium title with a ⋯ overflow menu (View /
// Edit / Unfollow) instead of a row of tiny links; a one-line selected-slot
// subtitle instead of the big "Next up" card; week + day chips (wrap — a
// horizontal scroll would fight the carousel pager); and a compact Start on
// its OWN row, right-aligned, NOT full-width and NOT titled with the session
// (the day chip + subtitle already say which).
//
// Start orchestration is platform work (mobile logger): when the host passes
// no onStart the run affordances (chips, Start, selected line) don't render and
// the card is management-only — that's the web mount, where the family doesn't
// log.
//
// NO delete here — from the In corso card you can only UNFOLLOW (deleting
// lives in My Programs, owner-only, behind a confirm).
export function ProgramRunCard({
  program,
  progress,
  catalog,
  resolveName,
  isStarting,
  onStart,
  onUnfollow,
  onEdit,
  isCurrentBlock = false,
  onSetCurrentBlock,
  theme,
  dark,
}: {
  program: ProgramT;
  progress: ProgramProgressT | null;
  catalog: Exercise[] | null;
  resolveName: (slug: string, snapshot?: string) => string;
  isStarting: boolean;
  /** Starts (weekIdx, sessionIdx) on the platform logger. Omit on hosts
   *  with no logger (web) — hides Start + the picker entirely. */
  onStart?: (weekIdx: number, sessionIdx: number) => void;
  onUnfollow: () => void;
  onEdit: () => void;
  /** AP-3: this program is the athlete's pinned "active block" — the one
   *  whose weekly targets score the Home meter. Drives the star toggle. */
  isCurrentBlock?: boolean;
  /** Toggle this program as the active block (pin / un-pin). Omit to hide the
   *  star (e.g. the web mount, where the family doesn't pick a block). */
  onSetCurrentBlock?: () => void;
  theme: UiTheme;
  dark: boolean;
}) {
  const { t } = useTranslation();
  const lang = useLocale();
  // PE-21 — resolved ONCE: the name reaches a <Text>, an i18n
  // interpolation and an a11y label, and the last two accept any value
  // without complaint, so an object would have rendered as
  // "[object Object]" with nothing to catch it.
  const programName =
    pickAuthoredText(program.name, lang, program.authorLocale) ?? "";
  const next = useMemo(() => nextSlotFor(program, progress), [program, progress]);
  // True once the athlete taps a day/week chip — stops the auto-resync below
  // from yanking their pick back to the computed next slot.
  const [touched, setTouched] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [weekIdx, setWeekIdx] = useState(next.weekIdx);
  const [sessionIdx, setSessionIdx] = useState(next.sessionIdx);
  const styles = makePlanStyles(theme);
  const canStart = onStart !== undefined;

  // Re-sync the pickers if the next slot shifts (e.g. progress fetch
  // landed after the row mounted). We only auto-update while the form
  // is closed — if the user opened it and changed selections, we don't
  // yank them.
  useEffect(() => {
    if (touched) return;
    setWeekIdx(next.weekIdx);
    setSessionIdx(next.sessionIdx);
  }, [next.weekIdx, next.sessionIdx, touched]);

  const selectedSession = program.sessions[sessionIdx];
  const selectedSessionLabel =
    pickAuthoredText(selectedSession?.name, lang, program.authorLocale) ??
    t("mobile.plan.sessionN", {
      n: sessionIdx + 1,
      defaultValue: "Session {{n}}",
    });

  const lastSession = progress && program.sessions[progress.lastSessionIdx];
  const lastSessionLabel =
    pickAuthoredText(
      lastSession ? lastSession.name : undefined,
      lang,
      program.authorLocale,
    ) ??
    (progress
      ? t("mobile.plan.sessionNShort", {
          n: progress.lastSessionIdx + 1,
          defaultValue: "S{{n}}",
        })
      : null);

  return (
    <View style={styles.cardRoot}>
      {/* Header: title + the "active block" star (AP-3). The star pins which
          followed program's weekly targets the Home meter scores against —
          a favourite-style toggle, not an action verb, so it lives here at
          the title level rather than in the Start/View/Edit action row. */}
      <View style={styles.programHead}>
        <Text style={styles.programName} numberOfLines={2}>
          {programName}
        </Text>
        {onSetCurrentBlock ? (
          <IconButton
            theme={theme}
            name="star"
            active={isCurrentBlock}
            activeGlyph="fill-pr"
            onPress={onSetCurrentBlock}
            accessibilityLabel={
              isCurrentBlock
                ? t("mobile.plan.currentBlockOn", {
                    defaultValue: "Current block — tap to unpin",
                  })
                : t("mobile.plan.currentBlockOff", {
                    defaultValue: "Set as current block",
                  })
            }
            accessibilityHint={t("mobile.plan.currentBlockHint", {
              defaultValue:
                "Pins this program's weekly targets to the Home meter",
            })}
          />
        ) : null}
      </View>

      {canStart ? (
        <>
          {/* Selected-slot subtitle — replaces the big "Next up" card. The day
              chips show only D-numbers, so the session name lives here. */}
          <Text style={styles.selectedLine} numberOfLines={1}>
            {t("mobile.plan.programNextInline", {
              week: weekIdx + 1,
              session: selectedSessionLabel,
              defaultValue: "Next: W{{week}} · {{session}}",
            })}
          </Text>
          {progress && lastSessionLabel ? (
            <Text style={styles.programLast}>
              {t("mobile.plan.programLastCompleted", {
                week: progress.lastWeekIdx + 1,
                session: lastSessionLabel,
                defaultValue: `Last: W{{week}} · {{session}}`,
              })}
            </Text>
          ) : null}

          {/* Week chips — chips for multi-week programs; single-cycle ones
              keep the SAME row with a plain "Single cycle" line instead
              (HR-9b, Antenore 2026-07-04: without it the carousel pages had
              different heights and the shorter card floated in blank space —
              the pager sizes every page to the tallest one). WRAP (never a
              horizontal ScrollView): a pager inside a pager fights the swipe
              gesture on mobile, and wrapping keeps the 44pt targets usable. */}
          <View style={styles.chipRow}>
            <Text style={styles.chipRowLabel}>
              {t("mobile.plan.programWeekLabel", { defaultValue: "Week" })}
            </Text>
            {program.cycleWeeks > 1 ? (
              <View style={styles.chipWrap}>
                {Array.from({ length: program.cycleWeeks }, (_, i) => {
                  const active = i === weekIdx;
                  return (
                    <Chip
                      key={i}
                      theme={theme}
                      shape="box"
                      style={{ minWidth: 36 }}
                      selected={active}
                      onPress={() => {
                        setTouched(true);
                        setWeekIdx(i);
                      }}
                      label={t("mobile.plan.programWeekShort", {
                        n: i + 1,
                        defaultValue: `W${i + 1}`,
                      })}
                    />
                  );
                })}
              </View>
            ) : (
              // Same height as a chip row (chips are 36 boxes) so single-cycle
              // and multi-week cards measure the same in the pager.
              <View style={{ minHeight: 36, justifyContent: "center" }}>
                <Text style={styles.programLast}>
                  {t("mobile.plan.singleCycle", {
                    defaultValue: "Single cycle, same every week",
                  })}
                </Text>
              </View>
            )}
          </View>

          {/* Day chips — always visible, one tap selects the session. ✓ marks
              the active one so the state isn't colour-only (a11y). */}
          <View style={styles.chipRow}>
            <Text style={styles.chipRowLabel}>
              {t("mobile.plan.programSessionLabel", { defaultValue: "Session" })}
            </Text>
            <View style={styles.chipWrap}>
              {program.sessions.map((s, i) => {
                const active = i === sessionIdx;
                return (
                  <Chip
                    key={s.id || i}
                    theme={theme}
                    shape="box"
                    style={{ minWidth: 36 }}
                    selected={active}
                    accessibilityLabel={
                      pickAuthoredText(s.name, lang, program.authorLocale) ??
                      undefined
                    }
                    onPress={() => {
                      setTouched(true);
                      setSessionIdx(i);
                    }}
                    label={t("mobile.plan.programDayShort", {
                      n: i + 1,
                      defaultValue: `D${i + 1}`,
                    })}
                  />
                );
              })}
            </View>
          </View>

        </>
      ) : null}

      {/* Actions — ONE row: the play Start (when runnable) aligned with View ·
          Edit · Unfollow (Antenore 2026-07-01: Start is a play icon on the same
          row as the others; no ⋯ menu). */}
      <View style={styles.actionRow}>
        {canStart ? (
          <Pressable
            accessibilityRole="button"
            accessibilityLabel={t("mobile.plan.programStartA11y", {
              session: selectedSessionLabel,
              defaultValue: "Start {{session}}",
            })}
            disabled={isStarting}
            onPress={() => onStart(weekIdx, sessionIdx)}
            style={({ pressed }) => [
              styles.startIconBtn,
              isStarting && styles.startButtonDisabled,
              pressed && styles.startButtonPressed,
            ]}
          >
            <Icon
              name="play"
              size={theme.typography.sizes.md}
              color={isStarting ? theme.palette.ink3 : theme.palette.inkOnBrand}
            />
          </Pressable>
        ) : null}
        <Pressable
          accessibilityRole="button"
          accessibilityState={{ expanded: showPreview }}
          accessibilityLabel={
            showPreview
              ? t("mobile.plan.hide", { defaultValue: "Hide" })
              : t("mobile.plan.view", { defaultValue: "View" })
          }
          onPress={() => setShowPreview((v) => !v)}
          style={({ pressed }) => [styles.actionIconBtn, pressed && styles.chipPressed]}
        >
          <Icon name="list" size={theme.typography.sizes.md} color={theme.palette.ink2} />
        </Pressable>
        <Pressable
          accessibilityRole="button"
          accessibilityLabel={t("program.edit", { defaultValue: "Edit" })}
          accessibilityHint={t("program.editing", { name: programName })}
          onPress={onEdit}
          style={({ pressed }) => [styles.actionIconBtn, pressed && styles.chipPressed]}
        >
          <Icon name="edit" size={theme.typography.sizes.md} color={theme.palette.ink2} />
        </Pressable>
        <Pressable
          accessibilityRole="button"
          accessibilityLabel={t("program.unfollow", { defaultValue: "Unfollow" })}
          accessibilityHint={t("mobile.plan.programUnfollowHint", {
            defaultValue: "Stop following this program (it stays in My programs)",
          })}
          onPress={onUnfollow}
          style={({ pressed }) => [styles.actionIconBtn, pressed && styles.chipPressed]}
        >
          <Icon name="x" size={theme.typography.sizes.md} color={theme.palette.ink3} />
        </Pressable>
      </View>

      {showPreview ? (
        <ProgramPreview
          program={program}
          catalog={catalog}
          resolveName={resolveName}
          theme={theme}
          dark={dark}
        />
      ) : null}
    </View>
  );
}
